import type { RecordKey } from '@ricoh/ria.client.core/types/core/data.type';

import DateTime from '@ricoh/ria.client.core/plugins/moment';
import { base64 } from '@ricoh/ria.client.core/utils/encrypt/base64';
import { generate } from 'short-uuid';

import type { NewKey, NewKeyPatterns, StaticId, TransformKeyHandler } from './key.type';

/**
 * New Static Id.
 *
 * @template TName Name of id.
 * @param {string} namespace Name space.
 * @param {string} name Name.
 *
 * @returns {string} New Static id.
 */
export const newStaticId = <TNamespace extends string, TName extends string>(namespace: TNamespace, name: TName): StaticId<TName> =>
  base64.encodeStr(`${namespace}:${name}`) as StaticId<TName>;

/**
 * NewKey Template.
 *
 * @param {RecordKey} key Key.
 *
 * @returns {NewKey} NewKey.
 */
export const newKeyTemplate = (key: RecordKey): NewKey => `$newid(${key.toString()})`;

/**
 * Verify NewKey.
 *
 * @param {RecordKey} key Key.
 *
 * @returns {false | NewKeyPatterns} Key is NewKey.
 */
export function isNewKey(key: RecordKey): false | NewKeyPatterns {
  const keyStr = key.toString();
  const keyExec = /\$newid\((.+?)\)/.exec(keyStr);
  const patterns = keyExec?.at(1)?.split('-') ?? [];
  if (patterns.length < 1 || patterns.length > 2) {
    return false;
  }

  const [id, trailing] = patterns;
  if (!id) {
    return false;
  }

  return {
    id,
    trailing,
  };
}

/**
 * Generate NewUUID.
 *
 * @param {boolean} trailing Trailing with timestamp.
 *
 * @returns {string} UUID.
 */
export function newUUID(trailing: boolean = false): string {
  const id = generate();
  if (trailing) {
    const now = DateTime().format(DateTime.config.format.key.time);
    return `${id}${now}`;
  }

  return id;
}

/**
 * Generate NewKey.
 *
 * @param {boolean} trailing Trailing with timestamp.
 *
 * @returns {string} NewKey.
 */
export function newKey(trailing: boolean = false): string {
  const id = newUUID(trailing);
  return newKeyTemplate(id);
}

/**
 * Transform NewKey.
 *
 * @param {string} key NewKey.
 * @param {TransformKeyHandler} handle Transform handler.
 *
 * @returns {string} Transformed NewKey.
 */
export function transformNewKey(key: RecordKey, handle: TransformKeyHandler): RecordKey {
  const verifiedNewKey = isNewKey(key);
  if (verifiedNewKey === false) {
    return key;
  }

  return handle(verifiedNewKey);
}

/**
 * Generate new file name.
 *
 * @param {string} extension File extension.
 *
 * @returns {string} New file name.
 */
export function newFileName(extension: string): string {
  const id = generate().substring(0, 8);
  const now = DateTime().format(DateTime.config.format.key.datetime);

  return `${id}-${now}.${extension}`;
}
